/**
 * Pantalla inicial integrada con resumen, selección de modo, rasgo y acento
 * Reemplaza UserPreferencesMenu con funcionalidad completa
 */

import { useEffect, useState } from 'react';
import { UserConfiguration, PracticeMode, AccentType, PersonalityType, MCERLevel, FunctionalityType } from '../../types';
import { StarRating } from './StarRating';
import { LevelProgress } from './LevelProgress';
import { StreakAchievementModal } from './StreakAchievementModal';
import { Avatar } from '../Avatar/Avatar';
import gamificationService from '../../services/gamificationService';
import configService from '../../services/configService';
import authService from '../../services/authService';
import { LevelProgress as LevelProgressType } from '../../types';
import './InitialScreen.css';

interface InitialScreenProps {
  /** Email pasado desde App tras configurar valores por defecto; evita que racha/tiempos/interacciones se carguen con email vacío */
  initialEmail?: string | null;
  onConfigurationComplete: (config: UserConfiguration) => void;
}

// Modos temporalmente deshabilitados
const DISABLED_MODES: PracticeMode[] = [
  'examen',
];

const isModeDisabled = (mode: PracticeMode): boolean => {
  return DISABLED_MODES.includes(mode);
};

const voiceDictionary: Record<string, { voice: string; language: string }> = {
  'Woman-Canadá': { voice: 'fr-CA-Chirp3-HD-Algenib', language: 'fr-CA' },
  'Woman-Francia': { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' },
  'Woman-Francia2': { voice: 'fr-FR-Neural2-C', language: 'fr-FR' },
  'Woman-Bélgica': { voice: 'fr-BE-CharlineNeural', language: 'fr-BE' },
};

// Función para obtener el prompt reference según el modo y el entorno
const getPromptReference = (mode: PracticeMode, isTestingPhase: boolean): string => {
  const suffix = isTestingPhase ? '-dev' : '-pro';
  const baseMap: Record<PracticeMode, string> = {
    'conversacional': 'alianzafrancesa-galia-conversacional',
    'estudio': 'alianzafrancesa-galia-ruta-academica',
    'examen': 'alianzafrancesa-galia-examen',
    'actividades-docente': 'alianzafrancesa-galia-actividades-docente',
    'actividades-estudiante': 'alianzafrancesa-galia-actividades-estudiante',
  };
  return `${baseMap[mode]}${suffix}`;
};

// Función para obtener el nombre de Galia según el modo
const getGaliaName = (mode: PracticeMode | null): string => {
  if (!mode) return 'Galia';
  
  const nameMap: Record<PracticeMode, string> = {
    'conversacional': 'Galia Conversacional',
    'estudio': 'Galia Estudio',
    'examen': 'Galia Examen',
    'actividades-docente': 'Galia Actividades',
    'actividades-estudiante': 'Galia Actividades',
  };
  
  return nameMap[mode] || 'Galia';
};

// Iconos de acentos usando emojis de banderas Unicode
const ACCENT_ICONS: Record<AccentType, string> = {
  'Francia': '🇫🇷',
  'Canadá': '🇨🇦',
  'Australia': '🇦🇺',
  'Bélgica': '🇧🇪',
};

// Iconos de personalidades usando caritas minimalistas emoji
const PERSONALITY_ICONS: Record<PersonalityType, string> = {
  'Neutral': '😊',
  'Amigable': '🤗',
  'Gruñona': '😠',
  'Sarcástica': '😏',
  'Graciosa': '😄',
};

export function InitialScreen({ initialEmail, onConfigurationComplete }: InitialScreenProps) {
  const [loading, setLoading] = useState(true);
  const [selectedMode, setSelectedMode] = useState<PracticeMode | null>('conversacional');
  const [selectedRasgo, setSelectedRasgo] = useState<PersonalityType | ''>('Amigable');
  const [selectedAcento, setSelectedAcento] = useState<AccentType | ''>('Francia');
  
  // Nuevos estados para personalización
  const [selectedMCER, setSelectedMCER] = useState<MCERLevel>('B1');
  const [selectedFunctionality, setSelectedFunctionality] = useState<FunctionalityType>('Conversadora');

  // Datos del usuario
  const [userTitle, setUserTitle] = useState<string | null>(null);
  const [streak, setStreak] = useState(0);
  const [streak5Count, setStreak5Count] = useState(0);
  const [levelProgress, setLevelProgress] = useState<LevelProgressType | null>(null);
  const [totalTime, setTotalTime] = useState(0);
  const [totalInteractions, setTotalInteractions] = useState(0);

  // Estados para opciones bloqueadas (Francia y Neutral siempre desbloqueados)
  const [unlockedAccents, setUnlockedAccents] = useState<AccentType[]>(['Francia']);
  const [unlockedPersonalities, setUnlockedPersonalities] = useState<PersonalityType[]>(['Neutral']);

  // Modal de racha de 5
  const [showStreak5Modal, setShowStreak5Modal] = useState(false);

  const config = configService.getConfig();
  const email = (initialEmail ?? config.email ?? '').trim();
  // Obtener role desde apolearnUserData (que ya tiene el role mapeado correctamente)
  const apolearnData = authService.getApolearnUserData();
  const userRole = apolearnData?.role || null;
  const roleLower = userRole?.toLowerCase() || '';
  // "Undefined" se trata como estudiante (no docente)
  const isUndefined = userRole === 'Undefined' || userRole === 'undefined';
  const isDocente = !isUndefined && (roleLower === 'teacher' || roleLower.includes('docente') || roleLower.includes('instructor') || roleLower.includes('profesor'));
  const isTestingPhase = config.testingPhase ?? true; // Por defecto dev

  // Cargar personalización guardada al montar
  useEffect(() => {
    configService.loadPersonalizationFromCache();
    const config = configService.getConfig();
    
    if (config.personalityType) {
      setSelectedRasgo(config.personalityType as PersonalityType);
    }
    if (config.functionalityType) {
      setSelectedFunctionality(config.functionalityType as FunctionalityType);
    }
    if (config.mcerLevel) {
      setSelectedMCER(config.mcerLevel as MCERLevel);
    }
  }, []);

  useEffect(() => {

    // Cargar datos completos de Apolearn (nivel, role, classrooms) al montar
    const loadApolearnData = async () => {
      // Verificar si ya hay datos
      const existingData = authService.getApolearnUserData();
      if (existingData && existingData.role) {
        return;
      }

      // Obtener email/userId de config
      const config = configService.getConfig();
      const emailToUse = config.email;
      const userIdToUse = config.userID;

      if (!emailToUse && !userIdToUse) {
        return;
      }

      try {
        await authService.getApolearnCompleteData(emailToUse || '', userIdToUse);
      } catch {
        // Error silencioso - los datos de Apolearn pueden no estar disponibles
      }
    };

    // Cargar datos de Apolearn en paralelo con otros datos
    loadApolearnData();

    const loadUserData = async () => {
      setLoading(true);
      if (!email) {
        setUnlockedAccents(['Francia']);
        setUnlockedPersonalities(['Neutral']);
        setLoading(false);
        return;
      }

      try {
        // Registrar conexión diaria y obtener racha
        const currentStreak = await gamificationService.registerDailyConnection(email);
        setStreak(currentStreak);

        // Verificar si alcanzó racha de 5
        const streak5Achievement = await gamificationService.checkStreak5Achievement(email, currentStreak);
        if (streak5Achievement) {
          setShowStreak5Modal(true);
          // Actualizar contador
          await gamificationService.checkStreak5Milestone(email, currentStreak);
        }

        // Cargar datos del usuario
        const [title, streak5, levelProg, time, interactions, accents, personalities] = await Promise.all([
          gamificationService.getCurrentTitle(email),
          gamificationService.getStreak5Count(email),
          gamificationService.getLevelProgress(email),
          gamificationService.getTotalPracticeTime(email),
          gamificationService.getTotalInteractions(email),
          gamificationService.getUnlockedAccents(email),
          gamificationService.getUnlockedPersonalities(email),
        ]);

        // const timeHours = Math.floor(time / 3600);
        // const timeMins = Math.floor((time % 3600) / 60);

        setUserTitle(title);
        setStreak5Count(streak5);
        setLevelProgress(levelProg);
        setTotalTime(time);
        setTotalInteractions(interactions);
        // Eliminar duplicados usando Set y asegurar que Francia y Neutral siempre estén incluidos
        const uniqueAccents = Array.from(new Set(['Francia', ...accents]));
        const uniquePersonalities = Array.from(new Set(['Neutral', ...personalities]));
        setUnlockedAccents(uniqueAccents as AccentType[]);
        setUnlockedPersonalities(uniquePersonalities as PersonalityType[]);
      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]); // Se ejecuta cuando cambia el email o cuando el componente se monta

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const handleModeSelect = (mode: PracticeMode) => {
    setSelectedMode(mode);
  };

  const handleRasgoSelect = async (rasgo: PersonalityType) => {
    if (!email) {
      setSelectedRasgo(rasgo);
      return;
    }

    const isUnlocked = await gamificationService.isPersonalityUnlocked(email, rasgo);
    if (isUnlocked) {
      setSelectedRasgo(rasgo);
    } else {
      const requiredLevel = gamificationService.getRequiredLevelForPersonality(rasgo);
      alert(`Esta personalidad requiere nivel ${requiredLevel}. ¡Sigue practicando para desbloquearla!`);
    }
  };

  const handleAcentoSelect = async (acento: AccentType) => {
    if (!email) {
      setSelectedAcento(acento);
      return;
    }

    const isUnlocked = await gamificationService.isAccentUnlocked(email, acento);
    if (isUnlocked) {
      setSelectedAcento(acento);
    } else {
      const requiredLevel = gamificationService.getRequiredLevelForAccent(acento);
      alert(`Este acento requiere nivel ${requiredLevel}. ¡Sigue practicando para desbloquearlo!`);
    }
  };

  const handleConfirm = () => {
    const mode = selectedMode ?? 'conversacional';
    const rasgo = selectedRasgo || 'Amigable';
    const acento = selectedAcento || 'Francia';

    const key = `Woman-${acento}`;
    const voiceInfo = voiceDictionary[key] || voiceDictionary['Woman-Francia'];

    // Guardar los nuevos parámetros en configService
    configService.setPersonalityType(rasgo);
    configService.setFunctionalityType(selectedFunctionality);
    configService.setMCERLevel(selectedMCER);

    const config: UserConfiguration = {
      mode,
      promptReference: getPromptReference(mode, isTestingPhase),
      rasgo, // Mantener por compatibilidad
      personalityType: rasgo as PersonalityType,
      functionalityType: selectedFunctionality,
      mcerLevel: selectedMCER,
      acento,
      voice: voiceInfo.voice,
      language: voiceInfo.language,
    };

    onConfigurationComplete(config);
  };

  const isAccentLocked = (acento: AccentType): boolean => {
    return !unlockedAccents.includes(acento);
  };

  const isPersonalityLocked = (personality: PersonalityType): boolean => {
    return !unlockedPersonalities.includes(personality);
  };

  const getRequiredLevel = (type: 'accent' | 'personality', value: string): number => {
    if (type === 'accent') {
      return gamificationService.getRequiredLevelForAccent(value as AccentType);
    } else {
      return gamificationService.getRequiredLevelForPersonality(value as PersonalityType);
    }
  };

  if (loading) {
    return (
      <div className="initial-screen-overlay">
        <div className="initial-screen-loading">
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="initial-screen-overlay">
      <div className="initial-screen">
        {/* Header: Resumen de datos */}
        <div className="initial-screen-header">
          <h1>¡Bienvenido al equipo AF!</h1>
          
          {userTitle && (
            <div className="user-title-card">
              <h2>{userTitle}</h2>
            </div>
          )}

          <div className="stats-grid">
            <div className="stats-row stats-row-racha">
              <div className="stat-card">
                <div className="stat-label">Racha</div>
                <StarRating streak={streak} animated={true} />
                <div className="stat-value">{streak} días</div>
                {streak5Count > 0 && (
                  <div className="stat-subvalue">Racha de 5: {streak5Count}x</div>
                )}
              </div>
            </div>
            <div className="stats-row stats-row-two-cols">
              <div className="stat-card">
                <div className="stat-label">Tiempo Total</div>
                <div className="stat-value">{formatTime(totalTime)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Interacciones</div>
                <div className="stat-value">{totalInteractions}</div>
              </div>
            </div>
            {levelProgress && (
              <div className="stats-row stats-row-nivel">
                <div className="stat-card stat-card-nivel">
                  <LevelProgress levelProgress={levelProgress} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selección de Rasgo (personalidad) */}
        <div className="initial-screen-section">
          <h3>Personalidad</h3>
          <div className="preferences-grid">
            {(['Amigable', 'Sarcástica'] as PersonalityType[]).map((rasgo) => {
              return (
                <button
                  key={rasgo}
                  type="button"
                  className={`preference-option ${selectedRasgo === rasgo ? 'selected' : ''}`}
                  onClick={() => setSelectedRasgo(rasgo)}
                >
                  <span className="preference-icon flag-icon" aria-hidden>
                    {PERSONALITY_ICONS[rasgo]}
                  </span>
                  <span className="preference-label">{rasgo}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selección de Nivel MCER */}
        <div className="initial-screen-section">
          <h3>Nivel MCER</h3>
          <div className="preferences-grid">
            {(['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as MCERLevel[]).map((nivel) => {
              return (
                <button
                  key={nivel}
                  type="button"
                  className={`preference-option ${selectedMCER === nivel ? 'selected' : ''}`}
                  onClick={() => setSelectedMCER(nivel)}
                >
                  <span className="preference-label">{nivel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selección de Funcionalidad */}
        <div className="initial-screen-section">
          <h3>Funcionalidad</h3>
          <div className="preferences-grid">
            {(['Profe', 'Conversadora'] as FunctionalityType[]).map((funcionalidad) => {
              return (
                <button
                  key={funcionalidad}
                  type="button"
                  className={`preference-option ${selectedFunctionality === funcionalidad ? 'selected' : ''}`}
                  onClick={() => setSelectedFunctionality(funcionalidad)}
                >
                  <span className="preference-label">{funcionalidad}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selección de Acento */}
        <div className="initial-screen-section">
          <h3>Acento</h3>
          <div className="preferences-grid">
            {(['Francia', 'Canadá'] as AccentType[]).map((acento) => {
              return (
                <button
                  key={acento}
                  type="button"
                  className={`preference-option ${selectedAcento === acento ? 'selected' : ''}`}
                  onClick={() => setSelectedAcento(acento)}
                >
                  <span className="preference-icon flag-icon" aria-hidden>
                    {ACCENT_ICONS[acento]}
                  </span>
                  <span className="preference-label">{acento}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Botón de confirmar */}
        <div className="initial-screen-footer">
          <button 
            className="confirm-button" 
            onClick={handleConfirm}
          >
            Continuar
          </button>
        </div>
      </div>

      {/* Modal de reconocimiento de racha de 5 */}
      {showStreak5Modal && (
        <StreakAchievementModal
          streak5Count={streak5Count}
          onClose={() => setShowStreak5Modal(false)}
        />
      )}
    </div>
  );
}

